/**
 * Shared constants for calendar configuration
 */

export const CalendarDefaults = {
  // Default office hours when not configured
  DEFAULT_MIN_TIME: '09:00:00',
  DEFAULT_MAX_TIME: '18:00:00',
  
  // Default session duration
  DEFAULT_SESSION_DURATION: '00:45:00',
  
  // Padding for calendar display
  TIME_PADDING: '00:15:00',
  
  // Day mapping
  DAYS_OF_WEEK: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const,
  
  // Day names for display
  DAY_NAMES: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const
};

/**
 * Builds the FullCalendar time-display options for a stored time-format setting.
 *
 * `time_format` comes from the day_view_settings entity (`field_time_format`,
 * surfaced via /api/dayview/calendar/settings as `time_format`): '12h' | '24h'.
 *
 * Returns both `slotLabelFormat` (axis labels) and `eventTimeFormat` (times shown
 * on events) so the whole calendar honors the same 12/24-hour choice. 24-hour uses
 * `hour12: false` and drops the meridiem; 12-hour keeps the AM/PM short meridiem.
 */
export function getCalendarTimeFormat(timeFormat: string | null | undefined): {
  slotLabelFormat: any;
  eventTimeFormat: any;
} {
  const is24h = timeFormat === '24h';

  if (is24h) {
    const fmt = {
      hour: '2-digit',
      minute: '2-digit',
      omitZeroMinute: false,
      hour12: false,
    };
    return { slotLabelFormat: fmt, eventTimeFormat: fmt };
  }

  const fmt = {
    hour: 'numeric',
    minute: '2-digit',
    omitZeroMinute: false,
    meridiem: 'short',
  };
  return { slotLabelFormat: fmt, eventTimeFormat: fmt };
}

/**
 * Maps backend day index (Monday=0) to calendar day index (Sunday=0)
 * Backend convention: Monday=0, Tuesday=1, ..., Sunday=6
 * Calendar convention: Sunday=0, Monday=1, ..., Saturday=6
 */
export function mapBackendDayToCalendarDay(backendIndex: number): number {
  // If Sunday (6 in backend), return 0 (Sunday in calendar)
  // Otherwise, add 1 to shift Monday-Saturday from 0-5 to 1-6
  return backendIndex === 6 ? 0 : backendIndex + 1;
}

/**
 * Maps calendar day index (Sunday=0) to backend day index (Monday=0)
 * Calendar convention: Sunday=0, Monday=1, ..., Saturday=6
 * Backend convention: Monday=0, Tuesday=1, ..., Sunday=6
 */
export function mapCalendarDayToBackendDay(calendarIndex: number): number {
  // If Sunday (0 in calendar), return 6 (Sunday in backend)
  // Otherwise, subtract 1 to shift Monday-Saturday from 1-6 to 0-5
  return calendarIndex === 0 ? 6 : calendarIndex - 1;
}
/**
 * Builds FullCalendar `businessHours` from the `office_hours` map returned by
 * /api/dayview/calendar/settings (day name → { start, end }). A closed day has
 * no entry, so it gets no business hours and FullCalendar shades it.
 * Returns false when no hours are configured, which leaves nothing shaded.
 */
export function buildOfficeBusinessHours(officeHours: any): any[] | false {
  const businessHours = CalendarDefaults.DAY_NAMES
    .map((dayName, index) => ({ hours: officeHours?.[dayName], index }))
    .filter(({ hours }) => hours?.start && hours?.end)
    .map(({ hours, index }) => ({ daysOfWeek: [index], startTime: hours.start, endTime: hours.end }));
  return businessHours.length ? businessHours : false;
}
